import classNames from 'classnames';
import Button from 'components/helpers/Button';
import { useCart } from 'components/helpers/Cart/CartHelper';
import { CartItem } from 'lib/util/cart';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import React, { useState } from 'react';
import useSafeSession from 'lib/util/useSafeSession';
import useDictionary from 'lib/sitecore/use-dictionary';
import { Routes } from '.generated/templates/Project.Routes.model';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  CartErrorDataLayerObject,
  gtmDatalayerPush,
  ProductItemRP,
  EcommerceDataLayerObject,
  gtmEcommerceDatalayerPush,
  alwaysSuppressLinkDataLayerPush,
} from 'lib/util/gtm-datalayer';

const MemoizedSvgIcon = React.memo(SvgIcon);

interface CartEntryProps {
  cartItem: CartItem;
  fetchUpdatedCart: () => void;
}

const CartEntry = ({ cartItem, fetchUpdatedCart }: CartEntryProps): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { data: session } = useSafeSession();
  const { fields } = sitecoreContext.route as unknown as Routes.Fields.CartPage;
  const { removeFromCart, validateQuantity, updateQuantity } = useCart();
  const [inputValue, setInputValue] = useState(cartItem.quantity);
  const [inputValid, validateInputValue] = useState(true);
  const dictionary = useDictionary();
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(Number(event.target.value));
  };
  const isNRCClaim = session?.authz_site?.some((item) => /^[lp]1_nrc_view_open$/.test(item));

  const maxQuantity = isNRCClaim
    ? (cartItem.itemMaxUs as number)
    : (cartItem.itemMaxIntl as number);

  const validationMessage = dictionary.Maximum_Order_Limit.replace('{0}', maxQuantity.toString());
  const productImage = {
    value: {
      src: cartItem?.image,
      alt: cartItem?.name,
      height: '525',
      width: '395',
    },
  };
  const quantityIsValid = (quantity?: number) => {
    const validateItemQuantity = validateQuantity(
      {
        id: cartItem.id,
        name: cartItem.name,
        price: cartItem.price,
        itemMin: cartItem.itemMin,
        itemNumber: cartItem.itemNumber,
        itemMaxIntl: cartItem.itemMaxIntl,
        itemMaxUs: cartItem.itemMaxUs,
        quantity: quantity || inputValue,
        image: cartItem.image,
        productLink: cartItem.productLink,
      },
      true
    );
    if (validateItemQuantity) {
      return true;
    }
    return false;
  };

  const handleCartMovementsGtmDatalayerPush = (event: string, quantityRemoved: number) => {
    const items: ProductItemRP[] = [
      {
        item_id: cartItem.itemNumber ?? cartItem.id,
        item_name: cartItem.name,
        index: '0',
        item_list_name: 'order materials',
        price: cartItem.price,
        quantity: quantityRemoved,
      },
    ];
    const gmtData: EcommerceDataLayerObject = {
      event: event,
      ecommerce: {
        value: '0',
        currency: 'USD',
        items: items,
      },
    };
    gtmEcommerceDatalayerPush(gmtData);
  };

  const handleIncrement = () => {
    setInputValue(inputValue + 1);
    if (quantityIsValid(inputValue + 1)) {
      validateInputValue(true);
      updateQuantity(cartItem.id as string, inputValue + 1);
    }
  };
  const handleDecrement = () => {
    if (inputValue > 0) {
      setInputValue(inputValue - 1);
      if (quantityIsValid(inputValue - 1)) {
        validateInputValue(true);
        updateQuantity(cartItem.id as string, inputValue - 1);
        handleCartMovementsGtmDatalayerPush('remove_from_cart', 1);
      }
    }
  };
  const handleRemoveFromCart = () => {
    handleCartMovementsGtmDatalayerPush('remove_from_cart', cartItem.quantity);
    removeFromCart(cartItem.id as string);
    fetchUpdatedCart();
  };
  const handleInputBlur = () => {
    if (quantityIsValid()) {
      validateInputValue(true);
      updateQuantity(cartItem.id as string, inputValue);
      if (cartItem.quantity > inputValue) {
        handleCartMovementsGtmDatalayerPush('remove_from_cart', cartItem.quantity - inputValue);
      }
    } else {
      validateInputValue(false);
      // set state of validation to false and perform GTM dataLayer push
      const gtmData: CartErrorDataLayerObject = {
        event: 'e_cartError',
        productName: (cartItem.name ?? null) as string | null,
        errorMessage: validationMessage,
      };
      gtmDatalayerPush(gtmData);
    }
  };
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^\d$/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete') {
      event.preventDefault();
    }
  };
  const textWhite = classNames(`text-white`);
  return (
    <>
      <div
        className={classNames(
          'mb-[26px] flex w-full border-t border-solid border-gray-midGray pt-[16px] md:pb-0 lg:max-w-[715px]'
        )}
      >
        <div className="">
          <div
            className={classNames(
              'h-[178px] w-[138px] md:h-[auto] md:w-[210px] lg:h-[383px] lg:w-[288px]'
            )}
          >
            <ImageWrapper
              field={productImage}
              className={classNames(
                'w-full rounded-[12px] !border !border-solid !border-gray-midGray'
              )}
            />
          </div>
        </div>
        <div className={classNames('ml-[16px] md:ml-[32px]')}>
          <Button
            linkField={{ value: { href: cartItem.productLink, text: cartItem.name } }}
            text={cartItem.name}
            variant="link"
            className={classNames('inline-block font-regular leading-4 text-blue md:mb-[3px]')}
          />
          <p className={classNames('mb-[12px] text-sm leading-4 md:mb-[16px]')}>
            Item #: {cartItem.itemNumber}
          </p>
          <div className={classNames('flex flex-col flex-wrap lg:flex-row')}>
            <div
              className={classNames(
                `quantity-selector-block order-1${!inputValid ? ' red-border' : ''}`
              )}
            >
              <div className={classNames('')}>
                <button
                  type="button"
                  onClick={handleDecrement}
                  disabled={inputValue <= 1}
                  className={classNames(
                    'quantity-minus',
                    `${inputValue <= 1 ? 'disabledButton' : ''}`
                  )}
                  aria-label="Remove One Item"
                >
                  <MemoizedSvgIcon
                    className={classNames(textWhite)}
                    icon="quantity-minus"
                    size="sm"
                    viewBoxSize="0 0 24 24"
                  />
                </button>
              </div>
              <input
                type="number"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                step="1"
                onKeyPress={handleKeyPress}
                className={classNames(
                  'w-[36px] appearance-none border-b border-solid border-[#A9A9A9] text-center outline-0'
                )}
              />
              <div>
                <button
                  type="button"
                  className={classNames(
                    'quantity-plus',
                    'quantity-plus',
                    `${
                      cartItem.itemMaxIntl === undefined || inputValue >= maxQuantity
                        ? 'disabledButton'
                        : ''
                    }`
                  )}
                  onClick={handleIncrement}
                  disabled={
                    inputValue >= maxQuantity // Compare the values
                  }
                  aria-label="Add One Item"
                >
                  <MemoizedSvgIcon
                    className={classNames(textWhite)}
                    icon="quantity-plus"
                    size="sm"
                    viewBoxSize="0 0 24 24"
                  />
                </button>
              </div>
            </div>
            {!inputValid && (
              <p
                className={classNames(
                  'order-2 mt-[8px] w-full text-sm leading-2 text-red-destructive-shade lg:order-3'
                )}
              >
                <span className={classNames('error-icon')}>
                  <SvgIcon
                    className={classNames(textWhite)}
                    icon="error"
                    size="xs"
                    viewBoxSize="0 0 16 16"
                  />
                </span>
                {validationMessage}
              </p>
            )}
            <Button
              onClick={handleRemoveFromCart}
              onGtmPush={alwaysSuppressLinkDataLayerPush}
              text={fields?.removeLabel.value}
              variant="link"
              className={classNames(
                'order-3 mt-[10px] flex self-start text-left lg:order-2 lg:mt-[0px] lg:self-center'
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default CartEntry;
